/**
 * InboxContext / InboxProvider
 *
 * The single mount point for the shared inbox state core (Option 3, finding 1).
 *
 * `InboxProvider` runs `useInbox()` EXACTLY ONCE and publishes its result through
 * React context. It is rendered by `ProtectedRoutesWrapper`, ABOVE the
 * `KeepAliveOutlet` route cache, so it is a true session singleton — it is NOT
 * duplicated per cached route. (Mounting it inside a cached layout such as
 * `MainRouteContainer` would instantiate one core per persistent main-nav route,
 * i.e. `/` and `/inbox`, reintroducing the double-fetch and a second chat
 * subscription.) The provider wraps the whole authenticated subtree, including
 * both the always-mounted `HomeTopBar` (whose `HomeTopBarHeader` drives the
 * unseen badge) AND the `<Outlet />` chain where `InboxPage` is lazily routed.
 * Because the provider lives for the whole authenticated session and is
 * independent of the active route, the inbox first pages are fetched ONCE and a
 * single chat message-nodes subscription stays open for the app lifetime — so:
 *
 *   - opening `/inbox` reads the ALREADY-fetched shared state (no second read of
 *     the first notifications page or first chat-nodes page), and
 *   - the TopBar unseen badge updates LIVE: an incoming chat message flips
 *     `hasUnreadMessage` inside the always-mounted core, whose derived
 *     `hasUnseenInbox` (read by every cached TopBar through this context)
 *     re-computes even while the Inbox is closed (finding 5).
 *
 * This replaces the former `useInboxUnseenBadgeInit` driver, which composed its
 * OWN `useInboxNotifications` + `useInboxChat` instances and double-fetched both
 * first pages. There is now only ONE such instance, here.
 *
 * Sharing the hook via context (rather than a Redux slice) is the
 * clean-architecture-friendly choice the finding endorsed: no app-level slice,
 * no new use case — just the existing `useInbox` core, mounted once and shared.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useInbox, type UseInboxResult } from '@/presentation/feature/inbox/hooks/useInbox';

const InboxContext = createContext<UseInboxResult | null>(null);

export const InboxProvider = ({ children }: { children: ReactNode }) => {
  // The ONE shared inbox core. Owns the auth-gated startup init (fetches the
  // first notifications + chat pages once), the combined `hasUnseenInbox` badge
  // truth every cached TopBar reads through this context, and the app-lifetime
  // chat subscription.
  const inbox = useInbox();

  return <InboxContext.Provider value={inbox}>{children}</InboxContext.Provider>;
};

/**
 * Read the shared inbox state. `InboxPage` consumes this instead of mounting its
 * own `useInbox`, so it renders the already-fetched shared state and never
 * re-fires the first-page fetches.
 */
export const useInboxContext = (): UseInboxResult => {
  const context = useContext(InboxContext);
  if (context === null) {
    throw new Error('useInboxContext must be used within an InboxProvider');
  }
  return context;
};
